import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3, Building2, CalendarDays, LayoutDashboard, LogOut,
  Menu, Settings, Shield, Ticket, Users, X
} from 'lucide-react';
import { cn } from '@/lib/utils';

const AppSidebar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isManager = isAdmin || user?.role === 'subadmin';

  const links = [
    { path: '/dashboard', label: 'Tickets', icon: Ticket, show: true },
    { path: '/maintenance', label: 'Maintenance', icon: CalendarDays, show: true },
    { path: '/admin', label: 'Overview', icon: LayoutDashboard, show: isManager },
    { path: '/admin/reports', label: 'Reports', icon: BarChart3, show: isManager },
    { path: '/admin/support-clients', label: 'Support Clients', icon: Users, show: isManager },
    { path: '/admin/companies', label: 'Companies', icon: Building2, show: isManager },
    { path: '/admin/users', label: 'Users', icon: Shield, show: isAdmin },
    { path: '/admin/settings', label: 'Settings', icon: Settings, show: isAdmin },
  ].filter(link => link.show);

  const isActive = path => (
    path === '/admin'
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  const openPage = path => {
    navigate(path);
    setMobileOpen(false);
  };

  const sidebarContent = (
    <div className="h-full flex flex-col bg-slate-950 text-white">
      <div className="h-20 px-5 flex items-center gap-3 border-b border-white/10">
        <img src="/favicon.png" alt="WM" className="w-10 h-10 rounded-xl bg-white" />
        <div>
          <p className="font-semibold leading-tight">Support Hub</p>
          <p className="text-xs text-slate-400">Work management</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        <p className="px-3 mb-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Workspace</p>
        {links.map(({ path, label, icon: Icon }) => (
          <button
            key={path}
            type="button"
            onClick={() => openPage(path)}
            className={cn(
              'w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              isActive(path)
                ? 'bg-white text-slate-950 shadow-sm'
                : 'text-slate-300 hover:bg-white/10 hover:text-white'
            )}
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2 mb-2">
          <p className="text-sm font-medium truncate">{user?.name || user?.email}</p>
          <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">{user?.role}</p>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-red-500/15 hover:text-red-200 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-40 w-64">
        {sidebarContent}
      </aside>

      <div className="lg:hidden fixed inset-x-0 top-0 z-40 h-14 bg-slate-950 text-white px-4 flex items-center justify-between shadow-md">
        <button type="button" onClick={() => setMobileOpen(true)} className="p-2 -ml-2 rounded-md hover:bg-white/10" aria-label="Open navigation">
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/favicon.png" alt="WM" className="w-7 h-7 rounded-lg bg-white" />
          <span className="text-sm font-semibold">Support Hub</span>
        </div>
        <div className="w-9" />
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <button type="button" className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />
          <aside className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] shadow-2xl">
            <button type="button" onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 z-10 p-2 rounded-md text-slate-400 hover:text-white hover:bg-white/10" aria-label="Close navigation">
              <X className="w-5 h-5" />
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
};

export default AppSidebar;
